import { http } from '@/lib/http';
import type {
  CartDto,
  CheckoutResponse,
  ProductAvailabilityDto,
  ProductDetailDto,
  StorefrontHomeResponse,
  StorefrontOrderDetail,
  StorefrontOrdersResponse,
  StorefrontSearchAccessResponse,
  StorefrontTaxonomyResponse,
} from './types';

/**
 * Rotas da vitrine na surplus-api. O `BASE_URL` do `http` já inclui `/api`,
 * então os paths aqui saem sem o prefixo — como no resto do app.
 */
export const storefrontApi = {
  searchAccess: () =>
    http.get<StorefrontSearchAccessResponse>('/storefront/search-access'),

  taxonomy: () =>
    http.get<StorefrontTaxonomyResponse>('/storefront/taxonomy'),

  /**
   * A home curada: dois destaques e as duas seções por gênero.
   *
   * Escopada por buyer pelo `Authorization` — a URL é a mesma para todo
   * mundo, e a resposta sai `private, no-store` por causa disso.
   */
  home: () => http.get<StorefrontHomeResponse>('/storefront/home'),

  /**
   * Disponibilidade da página visível. Id fora do escopo é OMITIDO da resposta
   * — o chamador compara com o que pediu e descarta o que não voltou.
   */
  availability: (productIds: string[]) =>
    http.post<ProductAvailabilityDto[]>('/storefront/products/availability', {
      product_ids: productIds,
    }),

  /** Peça esgotada responde 200 com `can_add: false`; fora do escopo, 404. */
  product: (productId: string) =>
    http.get<ProductDetailDto>(
      `/storefront/products/${encodeURIComponent(productId)}`
    ),

  cart: () => http.get<CartDto>('/storefront/cart'),

  /** SOMA unidades à linha, ou a cria. Cria o carrinho na primeira vez. */
  addToCart: (productId: string, quantity: number) =>
    http.post<CartDto>('/storefront/cart/items', {
      product_id: productId,
      quantity,
    }),

  /** Quantidade ABSOLUTA. Zerar não é o caminho de remover — o `DELETE` é. */
  setCartQuantity: (productId: string, quantity: number) =>
    http.patch<CartDto>(
      `/storefront/cart/items/${encodeURIComponent(productId)}`,
      { quantity }
    ),

  removeCartLine: (productId: string) =>
    http.del<CartDto>(
      `/storefront/cart/items/${encodeURIComponent(productId)}`
    ),

  checkout: () => http.post<CheckoutResponse>('/storefront/cart/checkout'),

  /**
   * Os pedidos deste buyer. O dono sai da SESSÃO — a rota não aceita id de
   * buyer, e é isso que separa `meus pedidos` de uma listagem de ofertas.
   */
  orders: () => http.get<StorefrontOrdersResponse>('/storefront/orders'),

  /** Pedido de outra pessoa responde 404, igual a pedido inexistente. */
  order: (orderId: string) =>
    http.get<StorefrontOrderDetail>(
      `/storefront/orders/${encodeURIComponent(orderId)}`
    ),
};
